import React, { useState, useEffect, useRef, useMemo } from 'react';
import Box from '@mui/material/Box';
import Fab from '@mui/material/Fab';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';

const PRIMARY = '#009587';
const PRIMARY_DARK = '#006157';
const PANEL_W = 460;
const PANEL_W_EXPANDED = 900;
const ASSISTANT_SOURCE = 'help-center';

function toOrigin(baseUrl) {
  if (!baseUrl) {
    return '';
  }
  try {
    return new URL(baseUrl).origin;
  } catch (err) {
    return String(baseUrl).replace(/\/$/, '');
  }
}

export default function AiAssistantIframe() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const iframeRef = useRef(null);

  const oldBaseUrl = process.env.REACT_APP_FIELDPIE_OLD_URL || '';
  const oldOrigin = useMemo(() => toOrigin(oldBaseUrl), [oldBaseUrl]);
  const panelUrl = `${String(oldBaseUrl).replace(/\/$/, '')}/helpcenterpanel/`;

  useEffect(() => {
    const onAssistantOpen = (event) => {
      if (event.detail !== ASSISTANT_SOURCE) {
        setOpen(false);
        setExpanded(false);
      }
    };
    window.addEventListener('fp-assistant-open', onAssistantOpen);
    return () => window.removeEventListener('fp-assistant-open', onAssistantOpen);
  }, []);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    window.dispatchEvent(new CustomEvent('fp-assistant-open', { detail: ASSISTANT_SOURCE }));
    return undefined;
  }, [open]);

  useEffect(() => {
    if (open && iframeRef.current && iframeRef.current.contentWindow && oldOrigin) {
      try {
        iframeRef.current.contentWindow.postMessage({ type: 'fp-hc-open' }, oldOrigin);
      } catch (err) {
      }
    }
    if (!open) {
      setExpanded(false);
    }
  }, [open, oldOrigin]);

  useEffect(() => {
    const onMessage = (e) => {
      const frameWin = iframeRef.current && iframeRef.current.contentWindow;
      if (!frameWin || e.source !== frameWin || !e.data) {
        return;
      }
      if (oldOrigin && e.origin !== oldOrigin) {
        return;
      }
      if (e.data.type === 'fp-hc-close') {
        setOpen(false);
        setExpanded(false);
      } else if (e.data.type === 'fp-hc-expand') {
        setExpanded(true);
      } else if (e.data.type === 'fp-hc-collapse') {
        setExpanded(false);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [oldOrigin]);

  return (
    <>
      <Fab
        aria-label={t('ai_open_help_center')}
        onClick={() => setOpen((current) => !current)}
        sx={{
          position: 'fixed',
          right: 24,
          bottom: 24,
          zIndex: 1200,
          width: 56,
          height: 56,
          padding: 0,
          minHeight: 56,
          overflow: 'hidden',
          backgroundColor: PRIMARY,
          color: '#fff',
          boxShadow: '0 6px 18px rgba(0,101,94,.35)',
          transition: 'transform .2s ease, box-shadow .2s ease',
          '&:hover': {
            backgroundColor: PRIMARY_DARK,
            transform: 'scale(1.06)',
            boxShadow: '0 8px 24px rgba(0,101,94,.45)'
          }
        }}
      >
        {open ? (
          <CloseIcon />
        ) : (
          <Box
            component="img"
            src="/img/helpcenter-logo.svg"
            alt=""
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              display: 'block',
              transform: 'scale(1.12)'
            }}
          />
        )}
      </Fab>

      <Box
        component="iframe"
        ref={iframeRef}
        title={t('ai_help_center')}
        src={panelUrl}
        allow="microphone"
        sx={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: `${expanded ? PANEL_W_EXPANDED : PANEL_W}px`,
          maxWidth: '100vw',
          height: '100vh',
          maxHeight: '100vh',
          border: 'none',
          borderRadius: 0,
          overflow: 'hidden',
          backgroundColor: '#fff',
          zIndex: 1300,
          boxShadow: '0 12px 40px rgba(0,0,0,.18)',
          transformOrigin: 'right center',
          transition: 'opacity .25s ease, transform .25s ease, visibility .25s, width .3s ease',
          opacity: open ? 1 : 0,
          visibility: open ? 'visible' : 'hidden',
          transform: open ? 'translateX(0)' : 'translateX(24px)',
          pointerEvents: open ? 'auto' : 'none'
        }}
      />
    </>
  );
}
